// Tabela dinâmica montada na tela de Relatórios: a prévia é calculada aqui com os dados já carregados e a mesma
// configuração vai para o backend (TabelaDinamicaExcel), que grava uma tabela dinâmica nativa do Excel.

export const FUNCOES_VALOR = [
    { id: "sum", label: "Soma", numerica: true },
    { id: "count", label: "Quantidade", numerica: false },
    { id: "average", label: "Média", numerica: true },
    { id: "max", label: "Máximo", numerica: true },
    { id: "min", label: "Mínimo", numerica: true },
];

export const AGRUPAR_DATAS = [
    { id: "dia", label: "Dia" },
    { id: "mes", label: "Mês" },
    { id: "ano", label: "Ano" },
];

// "Mostrar valores como" do Excel: o mesmo campo pode entrar duas vezes, uma em valor e outra em percentual
export const MOSTRAR_COMO = [
    { id: "normal", label: "Valor", sufixo: "" },
    { id: "percentTotal", label: "% do total geral", sufixo: " (% do total)" },
    { id: "percentColuna", label: "% do total da coluna", sufixo: " (% da coluna)" },
    { id: "percentLinha", label: "% do total da linha", sufixo: " (% da linha)" },
];

export const LAYOUTS = [
    { id: "tabular", label: "Tabular", ajuda: "Um campo por coluna, rótulo repetido em todas as linhas e subtotal no fim do grupo." },
    { id: "compacto", label: "Compacto", ajuda: "Campos das linhas numa coluna só, com recuo (padrão do Excel)." },
];

// Com 3 ou mais campos nas linhas: subtotal só do 1º (ex.: um total por negociador) ou de cada campo, menos o último
export const SUBTOTAIS = [
    { id: "primeiro", label: "Só do 1º campo" },
    { id: "todos", label: "Todos os grupos", ajuda: "Subtotal em cada campo das linhas, menos no último." },
];

export const LIMITES = { linhas: 4, colunas: 2, valores: 8, filtros: 8 };

export const configVazia = () => ({ linhas: [], colunas: [], valores: [], filtros: [], agruparDatas: "mes", layout: "tabular", subtotais: "primeiro" });

export const ehPercentual = (v) => Boolean(v.mostrarComo) && v.mostrarComo !== "normal";

// ---------- Várias tabelas dinâmicas (uma aba cada na planilha, todas sobre a mesma base) ----------

export const MAX_ABAS = 10;
const NOME_PADRAO_ABA = "Tabela dinâmica";
let contadorAbas = 0;

export const novaTabela = (base = {}) => {
    contadorAbas += 1;
    return { ...configVazia(), nome: "", ...base, id: `aba-${Date.now()}-${contadorAbas}` };
};

/** Consulta salva antiga (uma tabela só) ou nova (lista): sempre uma lista com ao menos uma aba. */
export function normalizarTabelas(valor) {
    const lista = Array.isArray(valor) ? valor : (valor ? [valor] : []);
    const tabelas = lista.filter(Boolean).slice(0, MAX_ABAS).map(t => novaTabela(t)); // id novo sempre
    return tabelas.length ? tabelas : [novaTabela()];
}

/**
 * Nome de cada aba como vai aparecer no Excel (mesma regra do backend): o digitado, sem : \ / ? * [ ], espaços
 * repetidos e até 31 letras, sem repetir (ganha " (2)"); sem nome, "Tabela dinâmica", "Tabela dinâmica 2"...
 */
export function nomesDasAbas(tabelas) {
    const usados = new Set(["relatório", "resumográfico"]);
    let semNome = 0;
    return tabelas.map(t => {
        let base = (t.nome || "").replace(/[\\/?*[\]:]/g, " ").replace(/\s+/g, " ").trim().slice(0, 31).trim();
        if (!base) {
            semNome += 1;
            base = semNome === 1 ? NOME_PADRAO_ABA : `${NOME_PADRAO_ABA} ${semNome}`;
        }
        let candidato = base;
        for (let n = 2; usados.has(candidato.toLowerCase()); n++) {
            const sufixo = ` (${n})`;
            candidato = `${base.slice(0, 31 - sufixo.length)}${sufixo}`;
        }
        usados.add(candidato.toLowerCase());
        return candidato;
    });
}

/** A aba sem o id da tela (que não vai para o backend nem para a consulta salva). */
export const semId = (tabela) => Object.fromEntries(Object.entries(tabela).filter(([chave]) => chave !== "id"));

/** O que vai para o backend: só as abas com conteúdo, já com o nome final, sem o id da tela. */
export function paraExportar(tabelas) {
    const nomes = nomesDasAbas(tabelas);
    return tabelas
        .map((t, i) => ({ ...t, nome: nomes[i] }))
        .filter(temConteudo)
        .map(semId);
}

export const temConteudo = (config) => Boolean(config && (config.linhas.length || config.colunas.length || config.valores.length));

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const PALAVRAS_DATA = ["data", "dt", "date", "vencimento", "pagamento", "baixa", "formalizacao", "criacao", "emissao", "nascimento", "distribuicao", "devolucao", "entrada"];
const PALAVRAS_MOEDA = ["valor", "vlr", "saldo", "desconto", "acrescimo", "honorario", "honorarios", "custas", "multa", "juros", "pago", "preco", "aberto"];

const palavras = (campo) => (campo || "").normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().split(/[^a-z0-9]+/);

/** Data vinda da API: epoch em ms (coluna com nome de data), texto ISO ou dd/MM/yyyy. */
export function comoData(valor, campo) {
    if (valor === null || valor === undefined || valor === "") return null;
    if (typeof valor === "number") {
        return valor > 946684800000 && valor < 3250368000000 && palavras(campo).some(p => PALAVRAS_DATA.includes(p)) ? new Date(valor) : null;
    }
    if (typeof valor !== "string") return null;
    const t = valor.trim();
    let m = t.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0));
    m = t.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return null;
}

/** "data", "numero" ou "texto", olhando os primeiros valores preenchidos da coluna. */
export function tipoDoCampo(campo, linhas) {
    let vistos = 0;
    let datas = 0;
    let numeros = 0;
    for (const linha of linhas || []) {
        const v = linha[campo];
        if (v === null || v === undefined || v === "") continue;
        vistos += 1;
        if (comoData(v, campo)) datas += 1;
        else if (typeof v === "number") numeros += 1;
        if (vistos >= 200) break;
    }
    if (vistos === 0) return palavras(campo).some(p => PALAVRAS_MOEDA.includes(p)) ? "numero" : "texto";
    if (datas === vistos) return "data";
    if (numeros === vistos) return "numero";
    return "texto";
}

export const ehMoeda = (campo) => palavras(campo).some(p => PALAVRAS_MOEDA.includes(p));

// Mesma lista do PlanilhaPadrao (mais título e forma de pagamento, que também são códigos)
const PALAVRAS_CODIGO = ["id", "codigo", "cod", "numero", "num", "nosso", "cpf", "cnpj", "documento", "dv", "ec", "cep",
    "telefone", "matricula", "protocolo", "processo", "titulo", "forma", "ano"];

/** Número que é código (ID, CPF/CNPJ, código do cliente, nosso número...): conta, mas não se soma. */
export const ehCodigo = (campo) => !ehMoeda(campo) && palavras(campo).some(p => PALAVRAS_CODIGO.includes(p));

/**
 * Colunas que dá para somar (gráfico e tabela dinâmica): números que não são código nem data, as de valor primeiro.
 * Antes entravam ID, documento, código do cliente e datas (que chegam como número): a soma dava números enormes.
 */
export function camposSomaveis(linhas) {
    if (!linhas || !linhas.length) return [];
    const campos = Object.keys(linhas[0]).filter(c => !ehCodigo(c) && tipoDoCampo(c, linhas) === "numero");
    return [...campos.filter(ehMoeda), ...campos.filter(c => !ehMoeda(c))];
}

/**
 * Sugestão inicial a partir do gráfico do relatório pronto: agrupa como o gráfico e soma/conta como ele, cada valor
 * com o seu percentual do total ao lado (o jeito mais comum de montar essa análise no Excel).
 */
export function sugestaoDoRelatorio(def, campos) {
    const g = def?.grafico;
    if (!g || !campos.includes(g.groupColumn)) return configVazia();
    const valores = [];
    if (g.aggregateType === "sum" && campos.includes(g.aggregateColumn)) {
        valores.push({ campo: g.aggregateColumn, funcao: "sum", mostrarComo: "normal" });
        valores.push({ campo: g.aggregateColumn, funcao: "sum", mostrarComo: "percentTotal" });
    }
    const contagem = campos.find(c => /^ID /i.test(c)) || g.groupColumn;
    valores.push({ campo: contagem, funcao: "count", mostrarComo: "normal" });
    valores.push({ campo: contagem, funcao: "count", mostrarComo: "percentTotal" });
    return { ...configVazia(), linhas: [g.groupColumn], valores };
}

/** Remove da configuração os campos que o resultado atual não tem (troca de fonte, consulta salva antiga). */
export function ajustarAosCampos(config, campos) {
    const tem = (c) => campos.includes(c);
    return {
        ...configVazia(),
        ...config,
        linhas: (config?.linhas || []).filter(tem),
        colunas: (config?.colunas || []).filter(tem),
        filtros: (config?.filtros || []).filter(tem),
        valores: (config?.valores || []).filter(v => tem(v.campo)),
    };
}

/** Chave e rótulo do item: datas agrupadas por dia/mês/ano, ordenáveis no tempo. */
function itemDe(valor, campo, tipo, agrupar) {
    if (valor === null || valor === undefined || valor === "") return { chave: "~vazio~", rotulo: "(vazio)", ordem: Infinity };
    if (tipo === "data") {
        const d = comoData(valor, campo);
        if (d) {
            if (agrupar === "ano") return { chave: String(d.getFullYear()), rotulo: String(d.getFullYear()), ordem: d.getFullYear() };
            if (agrupar === "mes") {
                const ordem = d.getFullYear() * 100 + d.getMonth();
                return { chave: String(ordem), rotulo: `${MESES[d.getMonth()]}/${d.getFullYear()}`, ordem };
            }
            const ordem = d.getFullYear() * 10000 + d.getMonth() * 100 + d.getDate();
            const pad = (n) => String(n).padStart(2, "0");
            return { chave: String(ordem), rotulo: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`, ordem };
        }
    }
    // Código (ID, ano...) sem separador de milhar: "2026", não "2.026"
    if (typeof valor === "number") return { chave: String(valor), rotulo: ehCodigo(campo) ? String(valor) : valor.toLocaleString("pt-BR"), ordem: valor };
    return { chave: String(valor), rotulo: String(valor), ordem: null };
}

const comparar = (a, b) => {
    if (a.ordem === Infinity || b.ordem === Infinity) return a.ordem === b.ordem ? 0 : a.ordem === Infinity ? 1 : -1;
    if (typeof a.ordem === "number" && typeof b.ordem === "number") return a.ordem - b.ordem;
    return a.rotulo.localeCompare(b.rotulo, "pt-BR", { numeric: true, sensitivity: "base" });
};

const compararListas = (a, b) => {
    for (let i = 0; i < a.length; i++) {
        const c = comparar(a[i], b[i]);
        if (c !== 0) return c;
    }
    return 0;
};

const novoAcumulador = () => ({ quantidade: 0, soma: 0, numeros: 0, min: null, max: null });

function acumular(acc, valor) {
    if (valor === null || valor === undefined || valor === "") return;
    acc.quantidade += 1;
    const n = typeof valor === "number" ? valor : null;
    if (n === null || Number.isNaN(n)) return;
    acc.soma += n;
    acc.numeros += 1;
    acc.min = acc.min === null ? n : Math.min(acc.min, n);
    acc.max = acc.max === null ? n : Math.max(acc.max, n);
}

function resultado(acc, funcao) {
    if (!acc) return null;
    switch (funcao) {
        case "sum": return acc.numeros ? acc.soma : null;
        case "average": return acc.numeros ? acc.soma / acc.numeros : null;
        case "max": return acc.max;
        case "min": return acc.min;
        default: return acc.quantidade;
    }
}

/**
 * Prévia da tabela dinâmica, do jeito que o Excel monta (os filtros do relatório não entram: no Excel viram listas no
 * topo da tabela). Com mais de um campo nas linhas há subtotal do 1º campo (com subtotais "todos", de cada campo menos
 * o último): no fim do grupo no layout tabular, no começo no compacto. No compacto, o grupo de um campo sem subtotal
 * ganha uma linha só com o nome ("grupo"), como no Excel. Valores em percentual são calculados sobre o total geral, da
 * coluna ou da linha.
 * Devolve { valores, colunas: [{ chave, rotulos }], linhas: [{ tipo: "item" | "subtotal" | "grupo", nivel, rotulos,
 * celulas: {chaveColuna: [valores]}, total: [valores] }], totalPorColuna, totalGeral, totalLinhas, cortadaLinhas,
 * cortadaColunas, temColunas, tabular }.
 */
export function calcularTabelaDinamica(dados, config, { maxLinhas = 200, maxColunas = 24 } = {}) {
    const linhasDados = dados || [];
    const valores = config.valores.length ? config.valores : (config.linhas[0] || config.colunas[0])
        ? [{ campo: config.linhas[0] || config.colunas[0], funcao: "count", mostrarComo: "normal" }] : [];
    const tipos = {};
    [...config.linhas, ...config.colunas].forEach(c => { tipos[c] = tipoDoCampo(c, linhasDados); });
    const nLinhas = config.linhas.length;
    const niveisComSubtotal = config.subtotais === "todos" ? Math.max(nLinhas - 1, 0) : Math.min(1, Math.max(nLinhas - 1, 0));
    const SEP = "|§|";

    const folhas = new Map();      // linha completa -> { itens, celulas: Map(coluna -> [acc]), total: [acc] }
    const subtotais = new Map();   // "nível#prefixo" -> idem, para os grupos dos campos de fora
    const colunas = new Map();     // coluna -> { itens, total: [acc] }
    const geral = valores.map(novoAcumulador);
    const novoGrupo = (itens) => ({ itens, celulas: new Map(), total: valores.map(novoAcumulador) });
    const somar = (grupo, chaveColuna, registro) => {
        if (!grupo.celulas.has(chaveColuna)) grupo.celulas.set(chaveColuna, valores.map(novoAcumulador));
        valores.forEach((v, i) => {
            acumular(grupo.celulas.get(chaveColuna)[i], registro[v.campo]);
            acumular(grupo.total[i], registro[v.campo]);
        });
    };
    const chaveSubtotal = (itens, k) => `${k}#${itens.slice(0, k + 1).map(i => i.chave).join(SEP)}`;

    linhasDados.forEach(registro => {
        const itensLinha = config.linhas.map(c => itemDe(registro[c], c, tipos[c], config.agruparDatas));
        const itensColuna = config.colunas.map(c => itemDe(registro[c], c, tipos[c], config.agruparDatas));
        const chaveLinha = itensLinha.map(i => i.chave).join(SEP);
        const chaveColuna = itensColuna.map(i => i.chave).join(SEP);

        if (!folhas.has(chaveLinha)) folhas.set(chaveLinha, novoGrupo(itensLinha));
        somar(folhas.get(chaveLinha), chaveColuna, registro);
        for (let k = 0; k < niveisComSubtotal; k++) {
            const chave = chaveSubtotal(itensLinha, k);
            if (!subtotais.has(chave)) subtotais.set(chave, novoGrupo(itensLinha.slice(0, k + 1)));
            somar(subtotais.get(chave), chaveColuna, registro);
        }
        if (!colunas.has(chaveColuna)) colunas.set(chaveColuna, { itens: itensColuna, total: valores.map(novoAcumulador) });
        valores.forEach((v, i) => {
            acumular(colunas.get(chaveColuna).total[i], registro[v.campo]);
            acumular(geral[i], registro[v.campo]);
        });
    });

    const colunasOrdenadas = [...colunas.entries()].sort((a, b) => compararListas(a[1].itens, b[1].itens));
    const colunasVisiveis = colunasOrdenadas.slice(0, maxColunas);
    const geralBruto = valores.map((v, i) => resultado(geral[i], v.funcao));
    const totalColunaBruto = Object.fromEntries(colunasOrdenadas.map(([chave, c]) => [chave, valores.map((v, i) => resultado(c.total[i], v.funcao))]));

    const pct = (a, b) => (a === null || a === undefined || !b ? null : a / b);
    // chaveColuna null = coluna de total da linha (o total dela é o total geral)
    const mostrar = (v, i, bruto, chaveColuna, totalLinha) => {
        switch (v.mostrarComo) {
            case "percentTotal": return pct(bruto, geralBruto[i]);
            case "percentColuna": return pct(bruto, chaveColuna === null ? geralBruto[i] : totalColunaBruto[chaveColuna][i]);
            case "percentLinha": return pct(bruto, totalLinha[i]);
            default: return bruto;
        }
    };
    const montarLinha = (grupo, tipo, nivel) => {
        const totalLinha = valores.map((v, i) => resultado(grupo.total[i], v.funcao));
        return {
            tipo,
            nivel,
            rotulos: grupo.itens.map(i => i.rotulo),
            celulas: Object.fromEntries(colunasVisiveis.map(([chave]) => [chave,
                valores.map((v, i) => mostrar(v, i, resultado(grupo.celulas.get(chave)?.[i], v.funcao), chave, totalLinha))])),
            total: valores.map((v, i) => mostrar(v, i, totalLinha[i], null, totalLinha)),
        };
    };

    // Compacto: grupo de campo sem subtotal = linha só com o nome, sem valores
    const linhaGrupo = (itens, nivel) => ({
        tipo: "grupo",
        nivel,
        rotulos: itens.map(i => i.rotulo),
        celulas: Object.fromEntries(colunasVisiveis.map(([chave]) => [chave, valores.map(() => null)])),
        total: valores.map(() => null),
    });

    const tabular = config.layout !== "compacto";
    const folhasOrdenadas = [...folhas.values()].sort((a, b) => compararListas(a.itens, b.itens));
    const mesmoGrupo = (a, b, k) => Boolean(a && b) && a.itens.slice(0, k + 1).every((it, j) => it.chave === b.itens[j].chave);
    const saida = [];
    folhasOrdenadas.forEach((folha, idx) => {
        const anterior = folhasOrdenadas[idx - 1];
        const proxima = folhasOrdenadas[idx + 1];
        if (!tabular) {
            for (let k = 0; k < nLinhas - 1; k++) {
                if (mesmoGrupo(anterior, folha, k)) continue;
                saida.push(k < niveisComSubtotal
                    ? montarLinha(subtotais.get(chaveSubtotal(folha.itens, k)), "subtotal", k)
                    : linhaGrupo(folha.itens.slice(0, k + 1), k));
            }
        }
        saida.push(montarLinha(folha, "item", Math.max(nLinhas - 1, 0)));
        if (tabular) {
            for (let k = niveisComSubtotal - 1; k >= 0; k--) {
                if (!mesmoGrupo(folha, proxima, k)) saida.push(montarLinha(subtotais.get(chaveSubtotal(folha.itens, k)), "subtotal", k));
            }
        }
    });

    return {
        valores,
        colunas: colunasVisiveis.map(([chave, c]) => ({ chave, rotulos: c.itens.map(i => i.rotulo) })),
        linhas: saida.slice(0, maxLinhas),
        totalPorColuna: Object.fromEntries(colunasVisiveis.map(([chave]) => [chave, valores.map((v, i) => {
            const bruto = totalColunaBruto[chave][i];
            if (v.mostrarComo === "percentColuna") return pct(bruto, bruto);
            return ehPercentual(v) ? pct(bruto, geralBruto[i]) : bruto;
        })])),
        totalGeral: valores.map((v, i) => (ehPercentual(v) ? pct(geralBruto[i], geralBruto[i]) : geralBruto[i])),
        totalLinhas: folhas.size,
        cortadaLinhas: saida.length > maxLinhas,
        cortadaColunas: colunasOrdenadas.length > maxColunas,
        temColunas: config.colunas.length > 0,
        tabular,
    };
}

/** Valor da célula como o Excel vai mostrar: % com 2 casas, R$ para dinheiro, inteiro para quantidade, 2 casas na média. */
export function formatarValor(valor, { campo, funcao, mostrarComo }) {
    if (valor === null || valor === undefined) return "";
    if (mostrarComo && mostrarComo !== "normal") return valor.toLocaleString("pt-BR", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (funcao === "count") return valor.toLocaleString("pt-BR");
    if (ehMoeda(campo)) return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    if (funcao === "average" || !Number.isInteger(valor)) return valor.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return valor.toLocaleString("pt-BR");
}

/** Nome do valor na tabela: o digitado pelo usuário ou o automático ("Soma de Valor (% do total)"), igual ao backend. */
export function nomeDoValor({ campo, funcao, mostrarComo, nome }, rotulo) {
    if (nome && nome.trim()) return nome.trim();
    const f = FUNCOES_VALOR.find(x => x.id === funcao) || FUNCOES_VALOR[1];
    const sufixo = (MOSTRAR_COMO.find(m => m.id === mostrarComo) || MOSTRAR_COMO[0]).sufixo;
    return `${f.label} de ${rotulo ? rotulo(campo) : campo}${sufixo}`;
}
